import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './lib/auth';
import { Toaster } from './components/ui/sonner';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import FloatingContact from './components/FloatingContact';
import OfferPopup from './components/OfferPopup';
import ThemeProvider from './lib/ThemeProvider';
import HomePage from './pages/HomePage';
import LocationsPage from './pages/LocationsPage';
import LocationDetailPage from './pages/LocationDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PoliciesPage from './pages/PoliciesPage';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLocations from './pages/admin/AdminLocations';
import AdminLocationForm from './pages/admin/AdminLocationForm';
import AdminListings from './pages/admin/AdminListings';
import AdminListingForm from './pages/admin/AdminListingForm';
import AdminSettings from './pages/admin/AdminSettings';
import AdminOffers from './pages/admin/AdminOffers';
import AdminMessages from './pages/admin/AdminMessages';
import AdminEditor from './pages/admin/AdminEditor';
import AdminTestimonials from './pages/admin/AdminTestimonials';
import API from './lib/api';
import { AlertTriangle } from 'lucide-react';

function MaintenancePage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4" data-testid="maintenance-page">
      <div className="max-w-lg w-full text-center">
        <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-red-500/10 flex items-center justify-center">
          <AlertTriangle className="w-10 h-10 text-red-400" />
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-white font-bold mb-4">
          Technical Error
        </h1>
        <p className="text-white/40 text-base sm:text-lg">
          Please check with your developer.
        </p>
      </div>
    </div>
  );
}

function AppLayout() {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [maintenance, setMaintenance] = useState(false);
  const [checked, setChecked] = useState(false);
  const isAdminPage = location.pathname.startsWith('/admin');
  const isLoginPage = location.pathname === '/login';

  useEffect(() => {
    API.get('/site-status').then(r => {
      setMaintenance(r.data.maintenance_mode === true);
      setChecked(true);
    }).catch(() => setChecked(true));
  }, [location.pathname]);

  // Show maintenance page for public visitors (not admin, not login/admin routes)
  if (checked && maintenance && !isAdminPage && !isLoginPage && !isAdmin) {
    return <MaintenancePage />;
  }

  return (
    <div className={`flex flex-col min-h-screen${!isAdminPage && !isLoginPage ? ' themed-page' : ''}`}>
      <Header />
      <main className={`flex-1 ${isLoginPage ? 'bg-stone-50 text-stone-800' : ''}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/locations" element={<LocationsPage />} />
          <Route path="/locations/:slug" element={<LocationDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/policies" element={<PoliciesPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/locations" element={<AdminLocations />} />
          <Route path="/admin/locations/new" element={<AdminLocationForm />} />
          <Route path="/admin/locations/:id/edit" element={<AdminLocationForm />} />
          <Route path="/admin/listings" element={<AdminListings />} />
          <Route path="/admin/listings/new" element={<AdminListingForm />} />
          <Route path="/admin/listings/:id/edit" element={<AdminListingForm />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/offers" element={<AdminOffers />} />
          <Route path="/admin/messages" element={<AdminMessages />} />
          <Route path="/admin/testimonials" element={<AdminTestimonials />} />
          <Route path="/admin/editor" element={<AdminEditor />} />
        </Routes>
      </main>
      <Footer />
      {!isAdminPage && !isLoginPage && <FloatingContact />}
      {!isAdminPage && !isLoginPage && <OfferPopup />}
      <ThemeProvider />
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppLayout />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
